import type { ReactNode } from 'react';
import type { MiaMessageChannel } from '#services/api/mia.ts';
import type { Recipient, Status } from './OutreachDialog.tsx';

import { faCalendarPlus } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@mui/material';

import {
  Content,
  DeliverySummary,
  Footer,
  GenerationNote,
  Header,
  MessagePreview,
  RecipientDetails,
  RecipientTableSection,
  Root,
} from './OutreachDialog.tsx';
import { pluralize } from './Readiness.utils.ts';

const channelLabels: Record<MiaMessageChannel, string> = {
  email: 'Email',
  sms: 'SMS',
};

interface ChannelPreview {
  body: ReactNode;
  channel: MiaMessageChannel;
  subject?: ReactNode;
}

interface CommonProps {
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  status: Status;
}

interface SingleRebookDialogProps extends CommonProps {
  children?: never;
  isBulk?: false;
  preview: ChannelPreview;
  recipient: Recipient;
  recipientCount?: never;
  template?: never;
}

interface BulkRebookDialogProps extends CommonProps {
  children: ReactNode;
  excludedPatientCount: number;
  isBulk: true;
  preview?: never;
  recipient?: never;
  recipientCount: number;
  template: ReactNode;
}

type RebookDialogProps = SingleRebookDialogProps | BulkRebookDialogProps;

export function RebookDialog(props: RebookDialogProps) {
  if (props.isBulk) {
    const personalizationNote = 'Bracketed details will be personalized for each appointment.';
    // TODO(multi-channel): "mobile number on file" mirrors Readiness' deliberate SMS-only gate.
    // Once backend channel execution availability is exposed, broaden this reason and the delivery
    // summary below with the eligibility rule.
    const recipientNote =
      props.excludedPatientCount > 0
        ? `${personalizationNote} Of the ${props.excludedPatientCount + props.recipientCount} patients who need to rebook, ${props.excludedPatientCount} will not be included because they do not currently need outreach or do not have a mobile number on file.`
        : personalizationNote;
    return (
      <Root onClose={props.onClose} maxWidth="lg">
        <Header color="warning" icon={faCalendarPlus}>
          Review rebooking outreach for {pluralize(props.recipientCount, 'patient')}
        </Header>

        <Content>
          <MessagePreview label="Messages will read similar to:">{props.template}</MessagePreview>
          <RecipientTableSection
            note={`${recipientNote} Each row represents an appointment. A patient may receive outreach about more than one appointment.`}
          >
            {props.children}
          </RecipientTableSection>

          {/* Bulk regenerates copy server-side per appointment, so its wording can drift from this
              template. */}
          <GenerationNote>
            Coordinate generates the final copy when you send, so wording may vary from the preview.
          </GenerationNote>

          <DeliverySummary>
            Sent via SMS · {pluralize(props.recipientCount, 'patient')} will be contacted about
            rebooking
          </DeliverySummary>
        </Content>

        <Footer
          color="warning"
          confirmLabel={props.confirmLabel}
          icon={faCalendarPlus}
          onConfirm={props.onConfirm}
          status={props.status}
        />
      </Root>
    );
  }

  return (
    <Root onClose={props.onClose}>
      <Header color="warning" icon={faCalendarPlus}>
        Review rebooking outreach
      </Header>

      <Content>
        <RecipientDetails recipient={props.recipient} />

        <MessagePreview label={`${channelLabels[props.preview.channel]} Preview`}>
          {props.preview.subject ? (
            <Typography component="p" variant="body2" fontWeight={600} mb={1}>
              Subject: {props.preview.subject}
            </Typography>
          ) : null}
          {props.preview.body}
        </MessagePreview>

        <DeliverySummary>
          Sent via {channelLabels[props.preview.channel]} · Patient will be contacted about
          rebooking
        </DeliverySummary>
      </Content>

      <Footer
        color="warning"
        confirmLabel={props.confirmLabel}
        icon={faCalendarPlus}
        onConfirm={props.onConfirm}
        status={props.status}
      />
    </Root>
  );
}
